import React, { useState, useEffect } from "react";
import {
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { getAdSlotName } from "../utils/adBannerData";
import AdBannerForm from "../components/AdBannerForm";
import BannerPreview from "../components/BannerPreview";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { translations } from "../utils/translations";
import useAdsList from "../stores/ads";
import { baseURL } from "../apis";

export default function AdManagement() {
  const [currentAd, setCurrentAd] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewAd, setPreviewAd] = useState(null);
  const [language, setLanguage] = useState("en");
  const t = translations[language] || translations.en;

  // --- 1. Ambil state dan actions dari Zustand ---
  const ads = useAdsList((state) => state.ads);
  const filters = useAdsList((state) => state.filters);
  const loaded = useAdsList((state) => state.loaded);
  const { getAds, addAd, deleteAd, updateAd, toggleAdStatus } = useAdsList(
    (state) => state.actions
  );

  // State lokal untuk input search, agar tidak memicu API call setiap ketikan
  const [localSearch, setLocalSearch] = useState("");

  // --- 2. Ganti logika fetching dan filtering ---
  useEffect(() => {
    // Cukup panggil getAds() saat komponen pertama kali dimuat.
    // getAds akan menggunakan filter default dari store.
    getAds();
  }, [getAds]);

  const getImageUrl = (image) => {
    if (!image) return "/images/default.png"; // optional fallback

    // If image already contains full URL
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    // Otherwise prefix with baseURL
    return baseURL + image;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    getAds({ search: localSearch, page: 1 }); // Kirim filter search ke API
  };

  const handleFilterChange = (filterName, value) => {
    getAds({ [filterName]: value, page: 1 }); // Kirim filter (status/lokasi) ke API
  };

  const handleSortChange = (e) => {
    const [sort_by, order] = e.target.value.split(",");
    getAds({ sort_by, order, page: 1 }); // Kirim filter sort ke API
  };

  // Hapus state loading lokal, langsung gunakan `!loaded` dari Zustand
  const isLoading = !loaded;

  // Modal handlers
  const openNewAdModal = () => {
    setCurrentAd(null);
    setIsModalOpen(true);
  };

  const openEditModal = (ad) => {
    setCurrentAd(ad);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentAd(null);
  };

  const openPreviewModal = (ad) => {
    setPreviewAd(ad);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewAd(null);
  };

  // Handler untuk Simpan, Hapus, dan Toggle Status (TIDAK ADA PERUBAHAN)
  const handleSaveAd = async (adData) => {
    if (currentAd) {
      const result = await updateAd(currentAd.id, adData);
      if (result.success) closeModal();
      else alert("Gagal mengupdate banner: " + JSON.stringify(result.error));
    } else {
      const result = await addAd(adData);
      if (result.success) closeModal();
      else alert("Gagal menyimpan banner: " + JSON.stringify(result.error));
    }
  };

  const handleDeleteAd = async (adId) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      const result = await deleteAd(adId);
      if (!result.success)
        alert("Failed to delete banner: " + JSON.stringify(result.error));
    }
  };

  const handleToggleStatus = async (adId) => {
    const result = await toggleAdStatus(adId);
    if (!result.success)
      alert("Failed to update status: " + JSON.stringify(result.error));
  };

  // --- HAPUS `filteredAds`, KARENA FILTERING DILAKUKAN OLEH BACKEND ---

  return (
    <div className="px-4 py-4 w-full">
      <div className="flex justify-between items-center mb-6">
        {/* Header (Tidak ada perubahan) */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {language === "en" ? "Ad Banner Management" : "バナー広告管理"}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {language === "en"
              ? "Manage advertisement banners displayed throughout the platform."
              : "プラットフォーム全体に表示される広告バナーを管理します。"}
          </p>
        </div>
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
      </div>

      {/* --- 3. Tambahkan UI untuk Filter dan Sort --- */}
      <div className="mb-6 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="relative w-full md:w-auto">
          <input
            type="text"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
            placeholder={
              language === "en" ? "Search banners..." : "バナーを検索..."
            }
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
          <button
            type="submit"
            className="absolute left-3 top-2.5 text-gray-400"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </form>

        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Filter by Status */}
          <select
            value={filters.is_active}
            onChange={(e) => handleFilterChange("is_active", e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-auto"
          >
            <option value="">
              {language === "en" ? "All Statuses" : "すべてのステータス"}
            </option>
            <option value="true">
              {language === "en" ? "Active" : "アクティブ"}
            </option>
            <option value="false">
              {language === "en" ? "Inactive" : "非アクティブ"}
            </option>
          </select>

          {/* Sort By */}
          <select
            value={`${filters.sort_by},${filters.order}`}
            onChange={handleSortChange}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-auto"
          >
            <option value="start_date,desc">
              {language === "en" ? "Date (Newest)" : "日付 (新しい順)"}
            </option>
            <option value="start_date,asc">
              {language === "en" ? "Date (Oldest)" : "日付 (古い順)"}
            </option>
            <option value="impressions,desc">
              {language === "en" ? "Impressions (High)" : "表示回数 (多い順)"}
            </option>
            <option value="clicks,desc">
              {language === "en" ? "Clicks (High)" : "クリック数 (多い順)"}
            </option>
          </select>

          {/* Add New Banner Button */}
          <button
            onClick={openNewAdModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md inline-flex items-center justify-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {language === "en" ? "Add New Banner" : "新しいバナーを追加"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      ) : ads.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500">
            {language === "en"
              ? "No banners found matching your criteria."
              : "条件に一致するバナーが見つかりません。"}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* Header Tabel (Tidak ada perubahan) */}
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Banner" : "バナー"}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Location" : "配置場所"}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Period" : "期間"}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Performance" : "パフォーマンス"}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Status" : "ステータス"}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {language === "en" ? "Actions" : "アクション"}
                  </th>
                </tr>
              </thead>
              {/* --- 4. Gunakan `ads` langsung dari Zustand --- */}
              <tbody className="bg-white divide-y divide-gray-200">
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 relative overflow-hidden rounded border border-gray-200">
                          <img
                            className="h-full w-full object-cover"
                            src={getImageUrl(ad.image)}
                            alt={ad.title}
                          />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 text-left">
                            {ad.title}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[180px] text-left">
                            {ad.target_url}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {/* PERBAIKAN: Gunakan ad.slot_location */}
                      <div className="text-sm text-gray-900 text-left">
                        {getAdSlotName(ad.slot_location)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 text-left">
                        {ad.start_date} ~ {ad.end_date}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-left">
                        <div>
                          {language === "en" ? "Impressions" : "表示回数"}:{" "}
                          <span className="font-medium">{ad.impressions}</span>
                        </div>
                        <div>
                          {language === "en" ? "Clicks" : "クリック数"}:{" "}
                          <span className="font-medium">{ad.clicks}</span>
                        </div>
                        <div>
                          CTR: <span className="font-medium">{ad.ctr}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {ad.is_active ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {language === "en" ? "Active" : "アクティブ"}
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {language === "en" ? "Inactive" : "非アクティブ"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      {/* Tombol Actions (Tidak ada perubahan) */}
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => handleToggleStatus(ad.id)}
                          className={`${
                            ad.is_active
                              ? "bg-green-500 hover:bg-green-600"
                              : "bg-yellow-500 hover:bg-yellow-600"
                          } text-white px-3 py-1 rounded-md`}
                          title={
                            ad.is_active
                              ? language === "en"
                                ? "Deactivate"
                                : "無効にする"
                              : language === "en"
                              ? "Activate"
                              : "有効にする"
                          }
                        >
                          {ad.is_active ? (
                            <CheckCircleIcon className="h-4 w-4" />
                          ) : (
                            <XCircleIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => openPreviewModal(ad)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
                          title={language === "en" ? "Preview" : "プレビュー"}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(ad)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
                          title={language === "en" ? "Edit" : "編集"}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAd(ad.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                          title={language === "en" ? "Delete" : "削除"}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Add/Edit (Tidak ada perubahan) */}
      {isModalOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={closeModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <AdBannerForm
                banner={currentAd}
                onSubmit={handleSaveAd}
                onCancel={closeModal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Preview (Tidak ada perubahan, tapi ada perbaikan data) */}
      {isPreviewModalOpen && previewAd && (
        <div className="fixed inset-0 overflow-y-auto z-50">
          <div className="flex items-center justify-center min-h-screen">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={closePreviewModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {language === "en" ? "Banner Preview" : "バナープレビュー"}
                  </h3>
                  <button
                    onClick={closePreviewModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <BannerPreview banner={previewAd} />
                <div className="mt-4 bg-gray-50 p-4 rounded-md grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === "en" ? "Title" : "タイトル"}:
                    </p>
                    <p className="font-medium">{previewAd.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === "en" ? "Location" : "配置場所"}:
                    </p>
                    <p className="font-medium">
                      {getAdSlotName(previewAd.slot_location)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === "en" ? "Target URL" : "ターゲットURL"}:
                    </p>
                    <p className="font-medium truncate">
                      {previewAd.target_url}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === "en" ? "Active Period" : "有効期間"}:
                    </p>
                    <p className="font-medium">
                      {previewAd.start_date} ~ {previewAd.end_date}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={closePreviewModal}
                >
                  {language === "en" ? "Close" : "閉じる"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
